import { useState, useEffect } from "react";
import {
  getProjects,
  createProject,
  deleteProject,
  updateProject,
  extractOrganizationId,
  type SimilarityProject,
} from "../api/similarity";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "./useToast";
import { dispatchTokenExpiredEvent } from "../utils/authEvents";

// Type for creating a new project
interface CreateProjectData {
  name: string;
  description: string;
  settings?: Partial<SimilarityProject["settings"]>;
}

export const useSimilarityProjects = () => {
  const [projects, setProjects] = useState<SimilarityProject[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<SimilarityProject | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const toast = useToast();

  const isAuthenticated = !!user;

  // Handle token expiration by logging out the user
  const handleTokenExpired = () => {
    dispatchTokenExpiredEvent();
  };

  // Load projects on mount and when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      loadProjects();
    } else {
      setProjects([]);
      setSelectedProject(null);
    }
  }, [isAuthenticated, user]);

  const loadProjects = async () => {
    if (!isAuthenticated || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      // Check if user has an organization
      const organizationId = extractOrganizationId(user);
      if (!organizationId) {
        throw new Error(
          "No organization found for your account. Please contact support.",
        );
      }

      const projectsData = await getProjects(user, handleTokenExpired);
      setProjects(projectsData);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load projects";
      setError(errorMessage);
      toast.error("Failed to load projects", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewProject = async (projectData: CreateProjectData) => {
    if (!isAuthenticated || !user) {
      toast.error(
        "Authentication required",
        "Please log in to create projects",
      );
      return null;
    }

    setIsCreating(true);
    setError(null);

    try {
      // Check if user has an organization
      const organizationId = extractOrganizationId(user);
      if (!organizationId) {
        throw new Error(
          "No organization found for your account. Please contact support.",
        );
      }

      const newProject = await createProject(
        projectData,
        user,
        handleTokenExpired,
      );
      await loadProjects(); // Refresh the projects list
      toast.success(
        "Project created successfully",
        `"${newProject.name}" is ready for similarity analysis`,
      );
      return newProject;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create project";
      setError(errorMessage);
      toast.error("Failed to create project", errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  };

  const updateExistingProject = async (
    projectId: string,
    updates: Partial<CreateProjectData>,
  ) => {
    if (!isAuthenticated || !user) {
      toast.error(
        "Authentication required",
        "Please log in to update projects",
      );
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const updatedProject = await updateProject(
        projectId,
        updates,
        user,
        handleTokenExpired,
      );
      await loadProjects(); // Refresh the projects list
      toast.success("Project updated successfully");
      return updatedProject;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update project";
      setError(errorMessage);
      toast.error("Failed to update project", errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteExistingProject = async (projectId: string) => {
    if (!isAuthenticated || !user) {
      toast.error(
        "Authentication required",
        "Please log in to delete projects",
      );
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await deleteProject(projectId, user, handleTokenExpired);
      await loadProjects(); // Refresh the projects list

      // Clear selected project if it was deleted
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }

      toast.success("Project deleted successfully");
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete project";
      setError(errorMessage);
      toast.error("Failed to delete project", errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshProjects = () => {
    loadProjects();
  };

  // Computed values
  const hasProjects = projects.length > 0;
  const activeProjects = projects.filter((p) => p.is_active);
  const inactiveProjects = projects.filter((p) => !p.is_active);

  const stats = {
    total: projects.length,
    active: activeProjects.length,
    inactive: inactiveProjects.length,
  };

  return {
    projects,
    selectedProject,
    setSelectedProject,
    isLoading,
    isCreating,
    error,
    hasProjects,
    activeProjects,
    inactiveProjects,
    stats,
    isAuthenticated,
    createNewProject,
    updateExistingProject,
    deleteExistingProject,
    refreshProjects,
  };
};
